import { useSelector } from 'react-redux';
import { memo, useCallback, useEffect, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { EmptyState } from 'foundations-components/transitional/components/EmptyState';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { Illustration } from 'foundations-components/transitional/components/Illustration';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import ContentRoutes from 'ContentUtils/Routes';
import Categories, { CATEGORY_IDS } from 'ContentUtils/constants/Categories';
// @ts-expect-error Untyped module
import KnowledgeBaseButton from 'ui-addon-i18n/components/KnowledgeBaseButton';
import UsageTracker from 'ContentEditorUI/utils/UsageTracker';
import { getActiveDomain } from 'ContentEditorUI/redux/selectors/portalDomainsSelectors';
import { getPortalId } from 'ContentEditorUI/redux/selectors/authSelectors';
import { CONTENT_MANAGEMENT_ACTION } from '../../constants/contentManagement';
import { domainIsContentEditDomain } from 'ContentEditorUI/utils/domainsUtils';
// @ts-expect-error Untyped module
import { QuickBlogCreationModal } from 'ContentComponents/components/modals/QuickBlogCreationModal';
import { Link } from 'foundations-components/transitional/components/Link';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const USE_THEMES_LINK = 'https://knowledge.hubspot.com/website-pages/use-themes';
const EDIT_BLP_LINK = 'https://knowledge.hubspot.com/blog/edit-a-blog-listing-page';
const ZeroStateCTA = ({
  label,
  onClick,
  dataTestId
}) => {
  return /*#__PURE__*/_jsx(Button, {
    "data-test-id": dataTestId,
    onClick: onClick,
    use: "primary",
    marginBlockStart: "200",
    children: label
  });
};
const OnboardingZeroState = ({
  header,
  contentBody,
  mainCta,
  illustration,
  dataTestId
}) => /*#__PURE__*/_jsxs(ErrorMessage, {
  "data-test-id": dataTestId,
  type: "notFound",
  illustration: illustration,
  illustrationProps: {
    width: 150
  },
  title: header,
  padding: "800",
  children: [contentBody, /*#__PURE__*/_jsx(View, {
    marginBlockStart: "400",
    as: "div",
    children: mainCta
  })]
});
const DomainNotConfiguredForContentTypeState = ({
  contentTypeLangKey,
  domainName
}) => {
  return /*#__PURE__*/_jsx(OnboardingZeroState, {
    dataTestId: "domain-not-configured-for-content-type",
    header: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `contentManagementPanel.table.domainNotConfiguredForContentTypeState.${contentTypeLangKey}.title`
    }),
    contentBody: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `contentManagementPanel.table.domainNotConfiguredForContentTypeState.${contentTypeLangKey}.body`,
      options: {
        domainName
      }
    }),
    illustration: "task-queues",
    mainCta: /*#__PURE__*/_jsx(Button, {
      use: "primary",
      onClick: () => {
        UsageTracker.trackAsBeacon('editorInteraction', {
          action: CONTENT_MANAGEMENT_ACTION,
          event: 'clicked-zero-state-update-domain-settings-button'
        });
        window.location.href = ContentRoutes.settings('domains');
      },
      marginBlockStart: "200",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "contentManagementPanel.table.domainNotConfiguredForContentTypeState.cta"
      })
    })
  });
};
const getIsDomainNotConfiguredForCurrentContentType = (contentDomain, contentType) => {
  switch (contentType) {
    case Categories.landing_page.id:
      return domainIsContentEditDomain(contentDomain) ? !contentDomain.is_used_for_landing_page : !contentDomain.isUsedForLandingPage;
    case Categories.site_page.id:
      return domainIsContentEditDomain(contentDomain) ? !contentDomain.is_used_for_site_page : !contentDomain.isUsedForSitePage;
    case Categories.blog_post.id:
      return domainIsContentEditDomain(contentDomain) ? !contentDomain.is_used_for_blog_post : !contentDomain.isUsedForBlogPost;
    default:
      return false;
  }
};
const PagesZeroState = /*#__PURE__*/memo(({
  contentTypeId
}) => {
  const contentDomain = useSelector(getActiveDomain);
  const portalId = useSelector(getPortalId);
  const domainName = contentDomain.domain;
  const contentTypeKey = contentTypeId === Categories.landing_page.id ? 'landingPages' : 'sitePages';
  const pageType = contentTypeId === Categories.landing_page.id ? 'landing-page' : 'site-page';
  useEffect(() => {
    UsageTracker.trackEditorInteraction({
      action: CONTENT_MANAGEMENT_ACTION,
      event: `view-${contentTypeKey}-zero-state`
    });
  }, [contentTypeKey]);
  if (getIsDomainNotConfiguredForCurrentContentType(contentDomain, contentTypeId)) {
    return /*#__PURE__*/_jsx(DomainNotConfiguredForContentTypeState, {
      contentTypeLangKey: contentTypeKey,
      domainName: domainName
    });
  }
  const baseLangKey = `contentManagementPanel.table.onboardingZeroState.${contentTypeKey}`;
  return /*#__PURE__*/_jsx(OnboardingZeroState, {
    dataTestId: "pages-zero-state",
    contentBody: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `${baseLangKey}.body`
    }),
    illustration: "task-queues",
    header: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `${baseLangKey}.title`
    }),
    mainCta: /*#__PURE__*/_jsx(ZeroStateCTA, {
      dataTestId: "get-started",
      label: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "contentManagementPanel.table.onboardingZeroState.getStartedButton"
      }),
      onClick: () => {
        UsageTracker.trackEditorInteraction({
          action: CONTENT_MANAGEMENT_ACTION,
          event: `clicked-zero-${contentTypeKey}-state-get-started-button`
        });
        window.location.href = `/page-creation/${portalId}/create-page/${pageType}/template?siteDomain=${encodeURIComponent(domainName)}`;
      }
    })
  });
});
const BlogPostsZeroState = ({
  createBlogPost
}) => {
  const contentDomain = useSelector(getActiveDomain);
  useEffect(() => {
    UsageTracker.trackEditorInteraction({
      action: CONTENT_MANAGEMENT_ACTION,
      event: 'view-blog-posts-zero-state'
    });
  }, []);
  if (getIsDomainNotConfiguredForCurrentContentType(contentDomain, Categories.blog_post.id)) {
    return /*#__PURE__*/_jsx(DomainNotConfiguredForContentTypeState, {
      contentTypeLangKey: "blog",
      domainName: contentDomain.domain
    });
  }
  return /*#__PURE__*/_jsx(EmptyState, {
    "data-test-id": "blog-posts-zero-state",
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "contentManagementPanel.table.onboardingZeroState.blogPosts.title"
    }),
    primaryContent: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "contentManagementPanel.table.onboardingZeroState.blogPosts.body"
        })
      }), /*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "contentManagementPanel.table.onboardingZeroState.blogPosts.userGuide_jsx",
          elements: {
            Link
          }
        })
      }), /*#__PURE__*/_jsxs(ButtonWrapper, {
        children: [/*#__PURE__*/_jsx(Button, {
          onClick: createBlogPost,
          use: "primary",
          marginBlockStart: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "contentManagementPanel.table.onboardingZeroState.blogPosts.createCta"
          })
        }), /*#__PURE__*/_jsx(Button, {
          onClick: () => {
            UsageTracker.trackAsBeacon('editorInteraction', {
              action: CONTENT_MANAGEMENT_ACTION,
              event: 'clicked-zero-state-import-blog-button'
            });
            window.location.href = ContentRoutes.siteImporterCreate();
          },
          marginBlockStart: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "contentManagementPanel.table.onboardingZeroState.blogPosts.importCta"
          })
        })]
      })]
    }),
    secondaryContent: /*#__PURE__*/_jsx(Illustration, {
      name: "task",
      width: "65%",
      marginBlockStart: "300"
    }),
    secondaryContentWidth: 230,
    paddingBlockStart: "400"
  });
};
const BlogListingPageZeroState = ({
  createBlogListingPage
}) => {
  useEffect(() => {
    UsageTracker.trackEditorInteraction({
      action: CONTENT_MANAGEMENT_ACTION,
      event: 'view-blog-listing-page-zero-state'
    });
  }, []);
  return /*#__PURE__*/_jsx(EmptyState, {
    "data-test-id": "blog-listing-page-zero-state",
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "contentManagementPanel.table.onboardingZeroState.blogListingPage.title"
    }),
    primaryContent: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "contentManagementPanel.table.onboardingZeroState.blogListingPage.body"
        })
      }), /*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "contentManagementPanel.table.onboardingZeroState.blogListingPage.userGuide_jsx",
          elements: {
            KnowledgeBaseButton
          },
          options: {
            useThemesUrl: USE_THEMES_LINK,
            editBlpUrl: EDIT_BLP_LINK,
            external: true
          }
        })
      }), /*#__PURE__*/_jsx(ButtonWrapper, {
        children: /*#__PURE__*/_jsx(Button, {
          onClick: createBlogListingPage,
          use: "primary",
          marginBlockStart: "200",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "contentManagementPanel.table.onboardingZeroState.blogListingPage.createCta"
          })
        })
      })]
    }),
    secondaryContent: /*#__PURE__*/_jsx(Illustration, {
      name: "marketing-templates",
      width: "65%",
      marginBlockStart: "300"
    }),
    secondaryContentWidth: 230,
    paddingBlockStart: "400"
  });
};
export const ContentZeroState = ({
  contentTypeId,
  createBlogPost,
  createBlogListingPage
}) => {
  if (contentTypeId === Categories.blog_post.id) {
    return /*#__PURE__*/_jsx(BlogPostsZeroState, {
      createBlogPost: createBlogPost
    });
  } else if (contentTypeId === Categories.site_page.id || contentTypeId === Categories.landing_page.id) {
    return /*#__PURE__*/_jsx(PagesZeroState, {
      contentTypeId: contentTypeId
    });
  } else if (contentTypeId === Categories.blog_listing_page.id) {
    return /*#__PURE__*/_jsx(BlogListingPageZeroState, {
      createBlogListingPage: createBlogListingPage
    });
  }
  return null;
};
export const BlogZeroState = () => {
  const [quickCreateModalOpen, setQuickCreateModalOpen] = useState(false);
  useEffect(() => {
    UsageTracker.trackEditorInteraction({
      action: CONTENT_MANAGEMENT_ACTION,
      event: 'view-blog-zero-state'
    });
  }, []);
  const handleBlogCreateSuccess = useCallback((_blog, blogPost) => {
    window.location.href = ContentRoutes.edit(blogPost.id, CATEGORY_IDS.BLOG_POST);
  }, []);
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(OnboardingZeroState, {
      dataTestId: "blog-zero-state",
      contentBody: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "contentManagementPanel.table.onboardingZeroState.blog.body"
      }),
      header: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "contentManagementPanel.table.onboardingZeroState.blog.title"
      }),
      mainCta: /*#__PURE__*/_jsx(ZeroStateCTA, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "contentManagementPanel.table.onboardingZeroState.blog.cta"
        }),
        onClick: () => {
          UsageTracker.trackAsBeacon('editorInteraction', {
            action: CONTENT_MANAGEMENT_ACTION,
            event: 'clicked-zero-state-create-new-blog-button'
          });
          setQuickCreateModalOpen(true);
        }
      }),
      illustration: "add-on-website"
    }), quickCreateModalOpen && /*#__PURE__*/_jsx(QuickBlogCreationModal, {
      onBlogCreateSuccess: handleBlogCreateSuccess,
      onBlogCreateFail: () => {
        setQuickCreateModalOpen(false);
      },
      onCancel: () => {
        setQuickCreateModalOpen(false);
      }
    })]
  });
};